import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ProductSelector from "../../../parts/categories/product-selector";
import submit from "submit-form";
import {
  calculateFreeTrialDaysLeft,
  isCoachFreeTrial,
  isPullActiveCoach,
} from "../../../utils/user-helpers";
import { useRillContext } from "../../../rill/RillReact/share/base";

const SearchBar = ({ query, onChange, placeholder }) => {
  return (
    <input
      type="search"
      value={query}
      onChange={({ target: { value } }) => onChange(value)}
      placeholder={placeholder}
      data-expandable
    />
  );
};

const TutorialItem = ({ tutorial }) => {
  const { _id, name } = tutorial;

  const handleOpenTutorial = () => {
    submit("/actions/refresh?", {
      method: "GET",
      body: {
        "modal[type]": "tutorial-detail",
        selectedTutorial: _id,
      },
    });
  };

  return (
    <li onClick={() => handleOpenTutorial()}>
      <img src="/img/new-icons/play-with-gradient.svg" className="icon-green" />
      <p className="p1">{name}</p>
    </li>
  );
};

const CategoryItem = ({
  name,
  tutorials,
  noResults = false,
  showingResultsFor,
  noTutorialsForCategory,
  notFoundAlt,
  sorry,
  noResultFound,
}) => {
  return (
    <article>
      {noResults ? <p className="s1">{showingResultsFor}</p> : null}
      <p className="s1">{name}</p>
      {!noResults ? (
        <ul className="tutorials-container">
          {tutorials.length ? (
            tutorials.map((tutorial, idx) => (
              <TutorialItem tutorial={tutorial} key={idx} />
            ))
          ) : (
            <span className="no-tutorials">{noTutorialsForCategory}</span>
          )}
        </ul>
      ) : null}
      {noResults ? (
        <div className="not-found">
          <img src="/img/svg/not-found.svg" alt={notFoundAlt} />
          <span className="not-found-title">{sorry}</span>
          <span className="not-found-results">{noResultFound}</span>
        </div>
      ) : null}
    </article>
  );
};

const MSBUniversity = ({ allCategories, allVideos }) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();

  const {
    query: { product },
    activeUser,
  } = locals;
  const [query, setQuery] = useState("");

  const hasFreeTrial =
    isCoachFreeTrial(activeUser) && calculateFreeTrialDaysLeft(activeUser) > 0;
  const isActivePull = isPullActiveCoach(activeUser);
  const isActivePush = Boolean(activeUser.pushAccountInfo);

  const getCategoryVideos = (category) => {
    if (!allVideos) return [];
    return allVideos.filter((video) => video.category === category);
  };

  const handleSearchBar = (value) => {
    setQuery(value);
  };

  const filteredCategories = useMemo(
    () =>
      allCategories.filter((category) => {
        const matchByCategoryName = category.name
          .toLowerCase()
          .includes(query.toLowerCase());
        const matchByTutorialName = getCategoryVideos(category.name).some(
          (video) => video.name.toLowerCase().includes(query.toLowerCase())
        );
        return matchByCategoryName || matchByTutorialName;
      }),
    [query, allCategories]
  );

  return (
    <main className="msb-university">
      <header>
        <h3>{t("msbUniversity.welcomeTitle")}</h3>
        <p className="p5">{t("msbUniversity.welcomeDescription")}</p>
      </header>
      <div className="sub-header">
        <SearchBar
          query={query}
          onChange={handleSearchBar}
          placeholder={t("msbUniversity.searchPlaceholder")}
        />
        <ProductSelector
          selected={product}
          hasFreeTrial={hasFreeTrial}
          isActivePull={isActivePull}
          isActivePush={isActivePush}
        />
      </div>
      <section>
        {filteredCategories.map((category, idx) => {
          return (
            <CategoryItem
              name={category.name}
              tutorials={getCategoryVideos(category.name)}
              key={idx}
              showingResultsFor={t("msbUniversity.showingResultsFor")}
              noTutorialsForCategory={t("msbUniversity.noTutorialsForCategory")}
              notFoundAlt={t("msbUniversity.notFoundAlt")}
              sorry={t("msbUniversity.sorry")}
              noResultFound={t("msbUniversity.noResultFound")}
            />
          );
        })}
        {!filteredCategories.length && query.length ? (
          <CategoryItem
            name={query}
            noResults
            showingResultsFor={t("msbUniversity.showingResultsFor")}
            noTutorialsForCategory={t("msbUniversity.noTutorialsForCategory")}
            notFoundAlt={t("msbUniversity.notFoundAlt")}
            sorry={t("msbUniversity.sorry")}
            noResultFound={t("msbUniversity.noResultFound")}
          />
        ) : null}
      </section>
    </main>
  );
};

export default MSBUniversity;
